import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  fetchSecuritySearch,
  type Security,
  type SecuritySearchParams,
} from "@/apis/securitiesApi";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";

/** Keystrokes settle before a request goes out. */
const DEBOUNCE_MS = 250;

/** Security master changes when a feed lands, not per keystroke. */
const STALE_TIME_MS = 60_000;

/** Shorter than this never hits the API. One character matches too much of the index. */
export const MIN_SECURITY_NAV_QUERY_LENGTH = 2;

/** How many securities the header nav shows. The panel is a typeahead, not a page. */
export const SECURITY_NAV_RESULT_LIMIT = 8;

/**
 * A full CUSIP is eight alphanumeric characters plus a numeric check digit.
 * A nine-letter word such as "tennessee" stays a semantic query.
 */
const FULL_CUSIP = /^[A-Za-z0-9]{8}\d$/;

/** The columns a header-nav row renders. */
export interface SecurityNavHit {
  cusip: string;
  issuerName: string;
  securityTypeDescription: string;
  couponRate?: number;
  maturityDate?: string;
}

export interface SecurityNavSearchResult {
  items: SecurityNavHit[];
  totalCount: number;
  /** The total is a nearest-neighbour candidate count, not the number of matches. */
  totalCountApproximate: boolean;
  /** The server stopped counting early: `totalCount` is a minimum, shown as "N+". */
  totalCountIsLowerBound: boolean;
  /** True while the debounce timer or the request is still catching up. */
  isLoading: boolean;
  isError: boolean;
}

/**
 * Request for a typed query. A 9-character identifier ending in a check digit is an
 * exact CUSIP filter. Every other query is semantic search on the security index.
 */
export function securityNavSearchParams(query: string): SecuritySearchParams {
  const trimmed = query.trim();
  if (FULL_CUSIP.test(trimmed)) {
    return { cusip: trimmed.toUpperCase(), limit: SECURITY_NAV_RESULT_LIMIT };
  }
  return { semanticQuery: trimmed, limit: SECURITY_NAV_RESULT_LIMIT };
}

function toNavHit(security: Security): SecurityNavHit {
  return {
    cusip: security.cusip,
    issuerName: security.issuerName,
    securityTypeDescription: security.securityTypeDescription,
    couponRate: security.couponRate,
    maturityDate: security.maturityDate,
  };
}

/**
 * Debounced security search for the header nav. An empty or one-character
 * query does not fetch — the search endpoint would otherwise return the most
 * recently updated securities.
 */
export function useSecurityNavSearch(
  input: string,
  options: Readonly<{ enabled?: boolean }> = {},
): SecurityNavSearchResult {
  const enabled = options.enabled ?? true;
  const trimmed = input.trim();
  const debounced = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const canSearch = debounced.length >= MIN_SECURITY_NAV_QUERY_LENGTH;

  const query = useQuery({
    queryKey: ["security-nav-search", debounced],
    queryFn: async ({ signal }) => {
      const page = await fetchSecuritySearch(securityNavSearchParams(debounced), signal);
      return { ...page, items: page.items.map(toNavHit) };
    },
    enabled: enabled && canSearch,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  // keepPreviousData and the debounce would otherwise leave the last CUSIP list
  // clickable while the next query is still settling or in flight.
  const visible = enabled && canSearch && debounced === trimmed && !query.isPlaceholderData;
  return {
    items: visible ? query.data?.items ?? [] : [],
    totalCount: visible ? query.data?.totalCount ?? 0 : 0,
    totalCountApproximate: visible ? query.data?.totalCountApproximate ?? false : false,
    totalCountIsLowerBound: visible ? query.data?.totalCountIsLowerBound ?? false : false,
    isLoading:
      enabled &&
      trimmed.length >= MIN_SECURITY_NAV_QUERY_LENGTH &&
      (query.isFetching || query.isPlaceholderData || debounced !== trimmed),
    isError: enabled && canSearch && query.isError,
  };
}
